import type { FileCollectionConstraints, FileCollectionMetadata } from './types';

/** Bounded relay chunk, aligned with Google's 256 KiB resumable requirement. */
export const MAX_UPLOAD_CHUNK_BYTES = 8 * 1024 * 1024;
/** Reservation ceilings count cancelled attempts too. */
export const MAX_RESPONSE_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024;
/** Maximum upload attempts reserved against one response. */
export const MAX_RESPONSE_UPLOAD_FILES = 20;
/** Default and hard per-question ceilings, independent of AI input limits. */
export const DEFAULT_FILE_COLLECTION_CONSTRAINTS: FileCollectionConstraints = {
  maxFiles: 5,
  maxFileBytes: 512 * 1024 * 1024,
  maxTotalBytes: 1024 * 1024 * 1024,
  allowedMimeTypes: [],
};
/** Accept real MIME types and top-level wildcard categories, never Google conversion types. */
function validMime(value: unknown, wildcard: boolean): value is string {
  return typeof value === 'string' && value.length <= 127
    && /^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+*-]+$/.test(value)
    && !value.startsWith('application/vnd.google-apps.')
    && (!value.includes('*') || (wildcard && /^[a-z0-9!#$&^_.+-]+\/\*$/.test(value)));
}
/** Validate authored limits without silently widening a malformed configuration. */
export function isFileCollectionConstraints(value: unknown): value is FileCollectionConstraints {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return (['maxFiles', 'maxFileBytes', 'maxTotalBytes'] as const).every(key =>
    Number.isSafeInteger(item[key]) && Number(item[key]) > 0 && Number(item[key]) <= DEFAULT_FILE_COLLECTION_CONSTRAINTS[key])
    && Array.isArray(item.allowedMimeTypes) && item.allowedMimeTypes.length <= 50
    && item.allowedMimeTypes.every(mime => validMime(mime, true));
}
/** Normalize the display name and reject invalid or over-budget metadata before reservation. */
export function validateFileCollectionMetadata(value: FileCollectionMetadata, constraints: FileCollectionConstraints): FileCollectionMetadata {
  if (!isFileCollectionConstraints(constraints) || typeof value.name !== 'string'
    || !validMime(value.mimeType, false) || !Number.isSafeInteger(value.size)
    || value.size <= 0 || value.size > constraints.maxFileBytes || value.size > constraints.maxTotalBytes) {
    throw new Error('Invalid file metadata or file limits');
  }
  const name = value.name.replace(/[\x00-\x1f\x7f/\\]/g, '_').trim().slice(0, 255);
  if (!name || name === '.' || name === '..') throw new Error('A file name is required');
  if (constraints.allowedMimeTypes.length && !constraints.allowedMimeTypes.some(mime =>
    mime === value.mimeType || (mime.endsWith('/*') && value.mimeType.startsWith(mime.slice(0, -1))))) {
    throw new Error('This file type is not accepted');
  }
  return {name, mimeType:value.mimeType, size:value.size};
}
